// Module-level config for the terminal-dissolve effect. TimesAreChanging
// reads these values each frame to drive the SVG filter and the prompt's
// bloom + opacity animation.
//
// Method mirrors Bespalov's Thanos-snap codepen:
//   1. TWO turbulences — a "big" one (low frequency, sets the
//      direction chunks of the source drift) and a "fine" one (high
//      frequency, adds per-pixel grain so the chunks become dust).
//   2. Big noise's R and G channels are sharpened with feFuncR/feFuncG
//      (`slope=2 intercept=-0.4`) so the directional vector field is
//      strong enough to actually move pixels.
//   3. feMerge combines the two noises into one displacement map.
//   4. feDisplacementMap uses the combined map to scatter the source.
//   5. JS animates (scroll-driven): displacement scale 0 → maxDisplacement,
//      element transform 1 → 1+bloomScale, opacity 1 → 0 starting at
//      fadeStart of the dissolve range. easeOutCubic.
//   6. Big-noise seed is reseeded on each forward entry for variety.

export type DissolveConfig = {
  // Peak displacement scale at the end of the dissolve range. Codepen=300.
  maxDisplacement: number;
  // How much the prompt blooms outward (CSS scale) over the dissolve.
  // Codepen=0.2 (i.e. scale 1 → 1.2).
  bloomScale: number;
  // Fraction of the dissolve range where the prompt opacity starts
  // dropping. 0 = immediately, 1 = at the end. Codepen=0.5.
  fadeStart: number;
  // BIG noise (sets direction; chunks of source drift coherently).
  bigNoiseFreq: number; // codepen 0.015
  bigOctaves: number; // codepen 1
  // Steepening of big-noise R/G channels — turns the noise into a
  // strong directional vector field. slope multiplies, intercept biases.
  bigSlope: number; // codepen 2
  bigIntercept: number; // codepen -0.4
  // FINE noise (per-pixel grain; turns chunks into dust).
  fineNoiseFreq: number; // codepen 1
  fineOctaves: number; // codepen 2
};

// Defaults — also the values currently in use. Kept as a separate
// frozen object so the in-use config can be edited in one place.
export const DISSOLVE_DEFAULTS: Readonly<DissolveConfig> = Object.freeze({
  maxDisplacement: 300,
  bloomScale: 0.2,
  fadeStart: 0.5,
  bigNoiseFreq: 0.015,
  bigOctaves: 1,
  bigSlope: 2,
  bigIntercept: -0.4,
  fineNoiseFreq: 1,
  fineOctaves: 2,
});

export const dissolveConfig: DissolveConfig = { ...DISSOLVE_DEFAULTS };
